import type { apiClient } from '~/shared/api/data-ingestion-api'
import type { AuditTrailEventDescriptionField, DataExtractionEventProps } from '../../model'
import {
  computeCodeDiffFields,
  computeRemovedFields,
  computeScalarFieldDiff,
  getFieldLabel,
  getFieldType,
} from '../compute-field-diff'
import type { EventMeta } from '../pre-pass'
import { collectFieldSearchText } from './searchable-text-utils'

/**
 * Finds related tags event for legacy (non-tagging-enabled) flow.
 * Port of findRelatedTagsEvent from the original monolithic transform.
 */
function findRelatedTagsEvent(events: apiClient.AuditEvent[], index: number) {
  const currentEventType = events[index].event_type
  const primaryEventType: apiClient.AuditEventType =
    currentEventType === 'datallm' ? 'tagsllm' : 'tagsrev'
  const fallbackEventType: apiClient.AuditEventType =
    currentEventType === 'datallm' ? 'datallm' : 'datarev'

  // First pass: search for tags events
  for (let i = index + 1; i < events.length; i++) {
    const event = events[i]
    if (event.event_type === currentEventType && event.data_content?.type === 'denial') {
      break
    }
    if (event.event_type === primaryEventType && event.user?.sub === events[index].user?.sub) {
      return event
    }
  }

  // Second pass: search for data events if tags events not found
  for (let i = index + 1; i < events.length; i++) {
    const event = events[i]
    if (event.event_type === currentEventType && event.data_content?.type === 'denial') {
      break
    }
    if (event.event_type === fallbackEventType && event.user?.sub === events[index].user?.sub) {
      return event
    }
  }
  return null
}

function buildDataExtractionFields({
  filteredEvents,
  event,
  index,
  initial,
  isTaggingEnabled,
}: {
  filteredEvents: apiClient.AuditEvent[]
  event: apiClient.AuditEvent
  index: number
  initial?: boolean
  isTaggingEnabled: boolean
}): { fields: AuditTrailEventDescriptionField[] | null; notes: string | null } {
  const relatedTagsEvent = !isTaggingEnabled
    ? findRelatedTagsEvent(filteredEvents, index)
    : undefined
  const notes = (relatedTagsEvent?.notes || event.notes)?.replace(/^\[Mirror\] /, '') ?? null
  const prevEventIndex =
    filteredEvents
      .map((e, i) => ({ event: e, index: i }))
      .filter(
        ({ event: e }) =>
          (e.event_type === event.event_type || e.event_type === 'datallm') &&
          e.timestamp < event.timestamp &&
          e.data_content?.type === event.data_content?.type,
      )
      .pop()?.index ?? -1
  const prevEvent = prevEventIndex !== -1 ? filteredEvents[prevEventIndex] : undefined
  const prevRelatedTagsEvent =
    prevEventIndex !== -1 && !isTaggingEnabled
      ? findRelatedTagsEvent(filteredEvents, prevEventIndex)
      : undefined
  const dataFields = [
    ...(event.data_content?.fields || []),
    ...(relatedTagsEvent?.data_content?.fields || []),
  ]
  const prevDataFields = [
    ...(prevEvent?.data_content?.fields || []),
    ...(prevRelatedTagsEvent?.data_content?.fields || []),
  ]
  if (dataFields.length > 0) {
    const fields: AuditTrailEventDescriptionField[] = []
    dataFields.forEach((field) => {
      const fieldLabel = getFieldLabel(field.name)
      if (!fieldLabel) return
      const fieldType = getFieldType(field.name)
      const prevField = prevDataFields.find((f) => f.name === field.name)
      if (fieldType === 'carc' || fieldType === 'rarc' || fieldType === 'natera-tags') {
        const values = (field.value || []) as (apiClient.CarcRarcCode | apiClient.TagInfo)[]
        const prevValues = (prevField?.value || []) as (
          | apiClient.CarcRarcCode
          | apiClient.TagInfo
        )[]
        fields.push(
          ...computeCodeDiffFields({
            fieldLabel,
            fieldType,
            currentValues: values,
            prevValues,
            currentEventType: event.event_type,
            prevEventType: prevEvent?.event_type,
          }),
        )
      } else {
        const result = computeScalarFieldDiff({
          fieldName: field.name,
          fieldLabel,
          fieldType,
          currentValue: field.value,
          prevField,
          prevEventType: prevEvent?.event_type,
          initial,
        })
        if (result) fields.push(result)
      }
    })
    fields.push(
      ...computeRemovedFields({
        prevDataFields,
        currentDataFields: dataFields,
        prevEventType: prevEvent?.event_type,
      }),
    )
    return { fields: fields.length > 0 ? fields : null, notes }
  }
  return { fields: null, notes }
}

export function transformDataExtraction(
  event: apiClient.AuditEvent,
  filteredEvents: apiClient.AuditEvent[],
  index: number,
  meta: EventMeta,
  isTaggingEnabled: boolean,
): DataExtractionEventProps {
  let title: string
  let fields: AuditTrailEventDescriptionField[] | null = null
  let notes: string | null = event.notes ?? null

  if (event.event_type === 'datallm') {
    title = 'Data Extraction: Data extracted'
    const result = buildDataExtractionFields({
      filteredEvents,
      event,
      index,
      initial: true,
      isTaggingEnabled,
    })
    fields = result.fields
    // For datallm, notes come from buildDataExtractionDetails too
    notes = result.notes
  } else if (event.event_type === 'datarev') {
    title = meta.dataExtractionDenialRevalidated
      ? 'Data Extraction: Fields revalidated'
      : 'Data Extraction: Fields validated'
    const result = buildDataExtractionFields({
      filteredEvents,
      event,
      index,
      isTaggingEnabled,
    })
    fields = result.fields
    notes = result.notes
  } else {
    // payors or payorchange shouldn't reach here, but fallback
    title = event.event_type
  }

  return {
    type: 'data-extraction',
    timestamp: event.timestamp,
    user: event.user,
    userRole: event.user ? 'person' : 'opti',
    step: 'data',
    eventType: event.event_type,
    title,
    fields,
    notes,
  }
}

export function getDataExtractionSearchableText(props: DataExtractionEventProps): string {
  const parts = [props.title]
  if (props.fields) parts.push(collectFieldSearchText(props.fields))
  if (props.notes) parts.push(props.notes)
  return parts.join(' ')
}
